import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Wifi, Users, Globe, MapPin, Smartphone, Tablet, Monitor, RefreshCw, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import * as Api from '../Services/Api';
import { normalizeSearchText } from '../utils/search';

const REFRESH_MS = 15 * 1000;
const ROWS_PER_PAGE = 10;

const relative = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
const formatAgo = (date) => {
  const seconds = Math.round((new Date(date).getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) return 'à l’instant';
  if (abs < 3600) return relative.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return relative.format(Math.round(seconds / 3600), 'hour');
  return relative.format(Math.round(seconds / 86400), 'day');
};
const formatDate = (date) => new Date(date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

const DEVICE_ICONS = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };
const DEVICE_LABELS = { mobile: 'Mobile', tablet: 'Tablette', desktop: 'Ordinateur' };

const joinPlace = (...parts) => parts.filter(Boolean).join(', ');

const osmLink = (gps) => `https://www.openstreetmap.org/?mlat=${gps.lat}&mlon=${gps.lng}#map=14/${gps.lat}/${gps.lng}`;

// City and country come from reverse geocoding the phone GPS; until (or if) that fails,
// the raw coordinates are shown instead.
const describeGps = (gps) => {
  const region = gps.region && gps.region !== gps.city ? gps.region : null;
  const place = joinPlace(gps.city, region, gps.country);
  const coords = `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}`;
  return {
    text: place || coords,
    detail: [place ? 'GPS' : 'Coordonnées GPS', gps.accuracy ? `±${gps.accuracy} m` : null].filter(Boolean).join(' · '),
  };
};

// Without GPS (refused or not answered yet), the city comes from the IP address.
const describeNetwork = (loc) => {
  if (!loc) return null;
  if (loc.isPrivate) return 'Réseau local';
  const region = loc.region && loc.region !== loc.city ? loc.region : null;
  return joinPlace(loc.city, region, loc.country) || null;
};

// Turns an API failure into a message that names the real cause.
const describeLoadError = (err) => {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.message;
  if (status === 401 || status === 403) {
    return {
      kind: 'auth',
      title: 'Session expirée',
      text: 'Votre connexion admin n’est plus valide. Reconnectez-vous pour voir les visiteurs.',
    };
  }
  if (status === 404) {
    return {
      kind: 'server',
      title: 'Fonction indisponible sur le serveur (404)',
      text: 'Le serveur ne connaît pas la route /api/visitors : le backend n’est pas à jour. Redémarrez ou redéployez la dernière version du backend.',
    };
  }
  if (status) {
    return {
      kind: 'server',
      title: `Erreur du serveur (${status})`,
      text: serverMessage ? `Réponse du serveur : ${serverMessage}` : 'Le serveur a rencontré un problème. Réessayez dans un instant.',
    };
  }
  return {
    kind: 'network',
    title: 'Serveur injoignable',
    text: typeof navigator !== 'undefined' && navigator.onLine === false
      ? 'Vous êtes hors connexion. Vérifiez le réseau puis réessayez.'
      : 'Aucune réponse du serveur. Vérifiez que le backend est démarré et que l’appareil est sur le même réseau, puis réessayez.',
  };
};

const AdminVisitors = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const [loadError, setLoadError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { visitor } | { all: true }

  const load = useCallback(async () => {
    try {
      const res = await Api.fetchVisitors();
      setData(res.data);
      setStatus('ready');
      setLoadError(null);
    } catch (err) {
      const nextError = describeLoadError(err);
      setLoadError(nextError);
      // keep showing the last list on a failed refresh, except when the session is gone
      setStatus((current) => (current === 'ready' && nextError.kind !== 'auth' ? 'ready' : 'error'));
    }
  }, []);

  const confirmDelete = async () => {
    const target = pendingDelete;
    if (!target) return;
    try {
      if (target.all) {
        const res = await Api.deleteAllVisitors();
        const deleted = res.data?.deleted ?? 0;
        toast.success(`${deleted} visite${deleted > 1 ? 's' : ''} supprimée${deleted > 1 ? 's' : ''}`);
      } else {
        await Api.deleteVisitor(target.visitor.id);
        toast.success('Visite supprimée');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 && !target.all) toast.info('Cette visite était déjà supprimée');
      else if (status === 401 || status === 403) toast.error('Session expirée : reconnectez-vous.');
      else toast.error('Suppression impossible. Réessayez.');
    }
    load();
  };

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const visitors = useMemo(() => {
    const list = data?.visitors || [];
    const q = normalizeSearchText(query);
    return list.filter((v) => {
      if (filter === 'online' && !v.online) return false;
      if (filter === 'gps' && !v.gps) return false;
      if (!q) return true;
      const haystack = normalizeSearchText([
        v.ip, v.gps?.city, v.gps?.region, v.gps?.country,
        v.location?.city, v.location?.region, v.location?.country,
        v.device?.os, v.device?.browser, DEVICE_LABELS[v.device?.kind],
      ].filter(Boolean).join(' '));
      return haystack.includes(q);
    });
  }, [data, filter, query]);

  useEffect(() => setPage(1), [filter, query]);

  const pageCount = Math.max(1, Math.ceil(visitors.length / ROWS_PER_PAGE));
  const current = Math.min(page, pageCount);
  const paged = visitors.slice((current - 1) * ROWS_PER_PAGE, current * ROWS_PER_PAGE);
  const stats = data?.stats || { online: 0, last24h: 0, countries: 0, gpsShared: 0, total: 0 };

  const cards = [
    { icon: Wifi, value: stats.online, label: 'En ligne maintenant' },
    { icon: Users, value: stats.last24h, label: 'Visiteurs (24 h)' },
    { icon: Globe, value: stats.countries, label: 'Pays' },
    { icon: MapPin, value: stats.gpsShared, label: 'Positions GPS partagées' },
  ];

  return (
    <>
      <div className="admin__results">
        <div className="admin__results__list">
          {cards.map(({ icon: Icon, value, label }) => (
            <div className="admin__results__list__card" key={label}>
              <div className="admin__results__list__card__images">
                <span className="admin__results__list__card__images__icones"><Icon size={20} /></span>
              </div>
              <div className="admin__results__list__card__texts">
                <h2 className="admin__results__list__card__title">{status === 'loading' ? '…' : value}</h2>
                <div className="admin__results__list__card__sub">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="admin__preview admin__visitors">
        <div className="admin__preview__header">
          <div className="admin__preview__header__search">
            <input
              type="text"
              placeholder="Rechercher une IP, une ville, un pays…"
              className="admin__preview__header__search__input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="admin__preview__header__filters admin__visitors__filters">
            <div className="admin__preview__header__filter">
              <span>Afficher</span>
              {[['all', 'Tous'], ['online', 'En ligne'], ['gps', 'Avec GPS']].map(([id, label]) => (
                <button key={id} type="button" className={filter === id ? 'active' : ''} onClick={() => setFilter(id)}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="admin__preview__header__buttons">
            <button type="button" className="admin__preview__header__buttons__btn admin__visitors__refresh" onClick={load}>
              <RefreshCw size={15} />
              <span>Actualiser</span>
            </button>
            <button
              type="button"
              className="admin__preview__header__buttons__btn admin__visitors__clear"
              onClick={() => setPendingDelete({ all: true })}
              disabled={!stats.total}
            >
              <Trash2 size={15} />
              <span>Tout supprimer</span>
            </button>
          </div>
        </div>

        <div className="admin__preview__content">
          {status === 'loading' ? (
            <div className="empty-state"><div className="loading-spinner" /></div>
          ) : status === 'error' ? (
            <div className="empty-state admin__visitors__error" role="alert">
              <p className="admin__visitors__error__title">{loadError?.title || 'Impossible de charger les visiteurs'}</p>
              <p className="admin__visitors__error__text">{loadError?.text}</p>
              {loadError?.kind === 'auth' ? (
                <button type="button" className="admin__visitors__error__btn" onClick={() => { localStorage.removeItem('token'); navigate('/login', { state: { from: { pathname: '/admin' } } }); }}>
                  Se reconnecter
                </button>
              ) : (
                <button type="button" className="admin__visitors__error__btn" onClick={load}>Réessayer</button>
              )}
            </div>
          ) : visitors.length === 0 ? (
            <div className="empty-state">
              {(data?.visitors || []).length === 0
                ? 'Aucun visiteur enregistré pour l’instant.'
                : 'Aucun visiteur ne correspond à ce filtre.'}
            </div>
          ) : (
            <>
              {loadError && (
                <div className="admin__visitors__stale" role="status">
                  Actualisation impossible — {loadError.title}. Dernière liste affichée.
                </div>
              )}
              <table className="admin__visitors__table">
                <thead>
                  <tr>
                    <th>Statut</th>
                    <th>Appareil</th>
                    <th>Adresse IP</th>
                    <th>Ville</th>
                    <th>Dernière activité</th>
                    <th>Sessions</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((v) => {
                    const DeviceIcon = DEVICE_ICONS[v.device?.kind] || Monitor;
                    const deviceDetail = [v.device?.os, v.device?.browser].filter(Boolean).join(' · ');
                    return (
                      <tr key={v.id}>
                        <td>
                          <span className={`admin__visitors__status ${v.online ? 'is-online' : ''}`}>
                            <span className="admin__visitors__dot" aria-hidden="true" />
                            {v.online ? 'En ligne' : 'Hors ligne'}
                          </span>
                        </td>
                        <td>
                          <span className="admin__visitors__device">
                            <DeviceIcon size={16} />
                            <span>
                              {DEVICE_LABELS[v.device?.kind] || 'Appareil'}
                              {deviceDetail && <small>{deviceDetail}</small>}
                            </span>
                          </span>
                        </td>
                        <td className="admin__visitors__ip">{v.ip || '—'}</td>
                        <td>
                          {v.gps ? (() => {
                            const place = describeGps(v.gps);
                            return (
                              <div className="admin__visitors__place">
                                <span>
                                  {place.text}
                                  {place.detail && <small>{place.detail}</small>}
                                </span>
                                <a
                                  className="admin__visitors__gps"
                                  href={osmLink(v.gps)}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={`Position partagée le ${formatDate(v.gps.at)}`}
                                >
                                  <MapPin size={13} />
                                  Voir sur la carte
                                </a>
                              </div>
                            );
                          })() : (() => {
                            const gpsStatus = v.gpsConsent === 'denied' ? 'GPS refusé' : 'GPS non partagé';
                            const network = describeNetwork(v.location);
                            if (!network) {
                              return (
                                <span className="admin__visitors__muted">
                                  {v.gpsConsent === 'denied' ? 'Refusé' : 'Position non partagée'}
                                </span>
                              );
                            }
                            return (
                              <div className="admin__visitors__place">
                                <span>
                                  {network}
                                  <small>{`D’après l’adresse IP · ${gpsStatus}`}</small>
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                        <td title={formatDate(v.lastSeen)}>{v.online ? 'Maintenant' : formatAgo(v.lastSeen)}</td>
                        <td>{v.visits}</td>
                        <td>
                          <button
                            type="button"
                            className="admin__visitors__delete"
                            onClick={() => setPendingDelete({ visitor: v })}
                            aria-label="Supprimer cette visite"
                            title="Supprimer cette visite"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="admin__preview__pagination">
                <div className="pagination__info">
                  {visitors.length} visiteur{visitors.length > 1 ? 's' : ''} · {stats.online} en ligne · actualisé toutes les 15 s
                </div>
                <div className="pagination__controls">
                  <button className="pagination__btn" onClick={() => setPage(Math.max(1, current - 1))} disabled={current === 1}>‹</button>
                  <div className="pagination__numbers">
                    <span className="pagination__number active">{current}</span>
                    <span className="pagination__dots">/ {pageCount}</span>
                  </div>
                  <button className="pagination__btn" onClick={() => setPage(Math.min(pageCount, current + 1))} disabled={current === pageCount}>›</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        {...(pendingDelete?.all ? {
          message: <>Supprimer <strong>toutes les visites ({stats.total})</strong> ?</>,
          warning: `Les ${stats.total} visite${stats.total > 1 ? 's' : ''} enregistrée${stats.total > 1 ? 's' : ''} seront définitivement effacées, positions GPS comprises. Cette action est irréversible.`,
          confirmLabel: `Supprimer les ${stats.total}`,
          confirmText: 'SUPPRIMER',
        } : {
          message: pendingDelete?.visitor ? (
            <>
              Supprimer la visite <strong>{DEVICE_LABELS[pendingDelete.visitor.device?.kind] || 'Appareil'}
                {(() => {
                  const where = pendingDelete.visitor.gps
                    ? describeGps(pendingDelete.visitor.gps).text
                    : describeNetwork(pendingDelete.visitor.location);
                  return where ? ` · ${where}` : '';
                })()}</strong>
              {' '}({formatAgo(pendingDelete.visitor.lastSeen)}) ?
            </>
          ) : null,
          warning: 'Cette visite et sa position éventuelle seront définitivement effacées.',
        })}
      />
    </>
  );
};

export default AdminVisitors;
